import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { IndicatorSectionIcon } from 'npo/pages/ReportBuilder/Components/ImpactSummary/SummaryIcon';
import {
  IndicatorSectionKey,
  stageIdToString,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  FeedbackCommentProps,
  FeedbackSectionId,
  RenderQuestionProps,
} from '../utils';
import ImpactDetailsQuestion from './ImpactDetailsQuestion';

const StyledIcon = styled.div<{ onClick?: () => unknown }>`
  ${({ onClick }) => onClick && 'cursor: pointer;'}
  display: flex;
  align-items: center;
  padding-top: ${SPACERS.small};
  padding-bottom: ${SPACERS.small};
  svg.icon {
    width: 40px;
    height: 40px;
    margin-right: ${SPACERS.tiny};
    path {
      stroke: ${COLORS.ctaBrandOrange};
    }
  }
`;

export const RenderStageIcon: React.FC<{
  sectionKey: IndicatorSectionKey;
  className?: string;
  onClick?: () => unknown;
}> = ({ onClick, sectionKey, className }) => {
  return (
    <StyledIcon className={className} onClick={onClick}>
      <IndicatorSectionIcon sectionKey={sectionKey} className="icon" />
      <TrueImpactText variant="body-small">
        <strong>Stage:</strong> {stageIdToString(sectionKey)}
      </TrueImpactText>
    </StyledIcon>
  );
};

export type CommentsByStage<T, U extends number> = {
  [sectionId in FeedbackSectionId]?: {
    [x in U]:
      | undefined
      | {
          [y in keyof Partial<T>]: undefined | FeedbackCommentProps[];
        };
  };
};

export type CommentDetails<T> = Omit<
  ImpactDetailStageComment<T>,
  'index' | 'comment'
>;

export const parseFeedbackCommentsWithDetails = <T, U extends number>(
  comments: FeedbackCommentProps[],
  parseCommentDetails: (details?: string) => CommentDetails<T> | undefined,
): CommentsByStage<T, U> => {
  const result: CommentsByStage<T, U> = {};
  for (const c of comments) {
    const details = parseCommentDetails(c.details);
    if (details) {
      const { sectionKey, indicatorId, subQuestion } = details;
      if (!result[sectionKey]) {
        result[sectionKey] = {};
      }
      if (!result[sectionKey][indicatorId]) {
        result[sectionKey][indicatorId] = {};
      }
      if (!result[sectionKey][indicatorId][subQuestion]) {
        result[sectionKey][indicatorId][subQuestion] = [];
      }
      result[sectionKey][indicatorId][subQuestion].push(c);
    }
  }
  return result;
};

const stateChange = <T, U extends number>(
  state: CommentsByStage<T, U>,
  {
    sectionKey,
    indicatorId,
    subQuestion,
    index,
    comment,
  }: ImpactDetailStageComment<T>,
): CommentsByStage<T, U> => {
  const newState: CommentsByStage<T, U> = { ...state };
  if (!newState[sectionKey]) {
    newState[sectionKey] = {};
  }
  if (!newState[sectionKey]![indicatorId]) {
    newState[sectionKey]![indicatorId] = {};
  }
  if (!newState[sectionKey]![indicatorId]![subQuestion]) {
    newState[sectionKey]![indicatorId]![subQuestion] = [];
  }
  if (
    index === undefined ||
    !newState[sectionKey]![indicatorId]![subQuestion]![index]
  ) {
    newState[sectionKey]![indicatorId]![subQuestion]!.push(comment);
  } else {
    newState[sectionKey]![indicatorId]![subQuestion]![index].text =
      comment.text;
    newState[sectionKey]![indicatorId]![subQuestion]![index].details =
      comment.details;
  }
  return newState;
};

export interface GenericFeedbackProps {
  reportId: number;
  questionId: number;
  sectionId: FeedbackSectionId;
  sectionKey: IndicatorSectionKey;
  currentUserId: RenderQuestionProps['currentUserId'];
  currentUserEmail: RenderQuestionProps['currentUserEmail'];
  currentUserName: RenderQuestionProps['currentUserName'];
  currentUserType: RenderQuestionProps['currentUserType'];
}

interface RenderOptionProps<T, U> extends GenericFeedbackProps {
  keys: (keyof T)[];
  item: T;
  itemTitle: (t: T) => React.ReactNode;
  IndicatorChildComponent?: {
    props: U;
    component: React.FC<
      U &
        GenericFeedbackProps & {
          itemKey: keyof T;
          item: T;
          index: number;
          totalItems: number;
        }
    >;
  };
  getIndicatorId: (o: T) => number;
  getTitle: (key: keyof T, o: T) => string;
  getExtraDetails: (key: keyof T, o: T) => React.ReactNode;
  getDescription: (key: keyof T, o: T) => React.ReactNode;
  onClick?: (key: keyof T, o: T) => unknown;
  handleDeleteStageComment: (
    details: CommentDetails<T>,
    index: number,
  ) => unknown;
  handleAddOrUpdateStageComment: (
    stateChange: ImpactDetailStageComment<T>,
  ) => unknown;
  getComments: ({
    sectionKey,
    indicatorId,
    subQuestion,
  }: CommentDetails<T>) => FeedbackCommentProps[];
}

const RenderOptions = <T, U>({
  item,
  keys,
  sectionKey,
  sectionId,
  reportId,
  questionId,
  currentUserEmail,
  currentUserId,
  currentUserName,
  currentUserType,
  IndicatorChildComponent,
  itemTitle,
  getIndicatorId,
  getComments,
  getTitle,
  getDescription,
  getExtraDetails,
  onClick,
  handleDeleteStageComment,
  handleAddOrUpdateStageComment,
}: RenderOptionProps<T, U>) => {
  return (
    <div className="stage-details">
      {itemTitle(item)}
      {keys.map((k, i) => {
        const indicatorId = getIndicatorId(item);
        const details: CommentDetails<T> = {
          sectionKey,
          indicatorId,
          subQuestion: k,
        };

        const comments = getComments(details);

        const handleComments = (
          comment: FeedbackCommentProps,
          index: number | undefined,
        ) => {
          handleAddOrUpdateStageComment({
            comment,
            index,
            ...details,
          });
        };

        const handleDelete = (index: number) => {
          handleDeleteStageComment(details, index);
        };

        return (
          <ImpactDetailsQuestion
            key={k?.toString() || i}
            comments={comments}
            reportId={reportId}
            sectionId={sectionId}
            details={JSON.stringify(details)}
            questionId={questionId}
            currentUserName={currentUserName}
            currentUserId={currentUserId}
            currentUserType={currentUserType}
            currentUserEmail={currentUserEmail}
            addOrUpdateComment={handleComments}
            deleteComment={handleDelete}
            title={getTitle(k, item)}
            extraDetails={getExtraDetails(k, item)}
            description={getDescription(k, item)}
            onClick={onClick ? () => onClick(k, item) : undefined}
            bottomComponent={
              IndicatorChildComponent &&
              IndicatorChildComponent.props && (
                <IndicatorChildComponent.component
                  {...IndicatorChildComponent.props}
                  reportId={reportId}
                  itemKey={k}
                  item={item}
                  index={i}
                  totalItems={keys.length}
                  questionId={questionId}
                  sectionKey={sectionKey}
                  currentUserName={currentUserName}
                  currentUserId={currentUserId}
                  currentUserType={currentUserType}
                  currentUserEmail={currentUserEmail}
                  sectionId={sectionId}
                />
              )
            }
          />
        );
      })}
    </div>
  );
};

export interface ImpactDetailStageComment<T> {
  sectionKey: IndicatorSectionKey;
  indicatorId: number;
  subQuestion: keyof T;
  index: number | undefined;
  comment: FeedbackCommentProps;
}

export interface ImpactDetailStageIconsProps<
  T,
  U extends object = object,
  V extends object = object
>
  extends Omit<
    RenderOptionProps<T, U>,
    | 'keys'
    | 'item'
    | 'handleDeleteStageComment'
    | 'handleAddOrUpdateStageComment'
  > {
  getKeys: (sectionKey: IndicatorSectionKey, o: T) => (keyof T)[];
  options: T[];
  ChildComponent?: {
    props: V;
    component: React.FC<V & GenericFeedbackProps>;
  };
  setCommentsByStage: React.Dispatch<
    React.SetStateAction<CommentsByStage<T, number>>
  >;
}

const StyledDiv = styled.div`
  .stage-details {
    padding-left: ${SPACERS.small};
    margin-bottom: ${SPACERS.medium};
    margin-left: ${SPACERS.extraTiny};
    border-left: 3px solid ${COLORS.greyBase};
  }
`;

const ImpactDetailStageIcons = <T, U extends object = object>({
  reportId,
  questionId,
  sectionKey,
  options,
  currentUserEmail,
  currentUserId,
  currentUserName,
  currentUserType,
  sectionId,
  IndicatorChildComponent,
  ChildComponent,
  getKeys,
  itemTitle,
  setCommentsByStage,
  getExtraDetails,
  getIndicatorId,
  getTitle,
  getDescription,
  getComments,
  onClick,
}: ImpactDetailStageIconsProps<T, U>) => {
  const [open, setOpen] = useState<boolean>(true);

  const handleToggle = useCallback(() => {
    setOpen(o => !o);
  }, [setOpen]);

  const handleAddOrUpdateStageComment = useCallback(
    (props: ImpactDetailStageComment<T>) => {
      setCommentsByStage(s => stateChange(s, props));
    },
    [setCommentsByStage, currentUserEmail, currentUserName],
  );

  const handleDeleteStageComment = useCallback(
    (details: CommentDetails<T>, index: number) => {
      setCommentsByStage(s => {
        const newState = { ...s };
        newState[details.sectionKey][details.indicatorId][
          details.subQuestion
        ].splice(index, 1);
        return newState;
      });
    },
    [setCommentsByStage, currentUserEmail, currentUserName],
  );

  if (options.length === 0) return null;
  return (
    <StyledDiv>
      <RenderStageIcon
        onClick={handleToggle}
        sectionKey={sectionKey}
        className="stage-name"
      />
      {open
        ? options.map(o => (
            <RenderOptions
              key={getIndicatorId(o)}
              item={o}
              reportId={reportId}
              questionId={questionId}
              sectionKey={sectionKey}
              currentUserName={currentUserName}
              currentUserId={currentUserId}
              currentUserType={currentUserType}
              currentUserEmail={currentUserEmail}
              sectionId={sectionId}
              itemTitle={itemTitle}
              handleDeleteStageComment={handleDeleteStageComment}
              getExtraDetails={getExtraDetails}
              keys={getKeys(sectionKey, o)}
              IndicatorChildComponent={IndicatorChildComponent}
              getComments={getComments}
              getIndicatorId={getIndicatorId}
              getTitle={getTitle}
              onClick={onClick}
              handleAddOrUpdateStageComment={handleAddOrUpdateStageComment}
              getDescription={getDescription}
            />
          ))
        : null}
      {ChildComponent && ChildComponent.props && (
        <ChildComponent.component
          {...ChildComponent.props}
          reportId={reportId}
          questionId={questionId}
          sectionKey={sectionKey}
          currentUserName={currentUserName}
          currentUserId={currentUserId}
          currentUserType={currentUserType}
          currentUserEmail={currentUserEmail}
          sectionId={sectionId}
        />
      )}
    </StyledDiv>
  );
};

export default ImpactDetailStageIcons;
